import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { requestAccountDeletion, cancelAccountDeletion } from '../services/api';
import { notify } from '../utils/notify';
import './AccountDeletion.css';

function AccountDeletion({ isOpen, onClose, initialStep = 'initial', initialScheduledDate = null }) {
  const [step, setStep] = useState(initialStep);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [scheduledDate, setScheduledDate] = useState(initialScheduledDate ? new Date(initialScheduledDate) : null);
  const [previousStep, setPreviousStep] = useState(null);

  // Update step and scheduledDate when props change
  useEffect(() => {
    if (isOpen) {
      setStep(initialStep);
      setScheduledDate(initialScheduledDate ? new Date(initialScheduledDate) : null);
      // Reset previousStep when modal opens
      setPreviousStep(null);
      // Reset loading state when modal opens
      setLoading(false);
    }
  }, [isOpen, initialStep, initialScheduledDate]);

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('ad-body-locked');
    }
    return () => {
      document.body.classList.remove('ad-body-locked');
    };
  }, [isOpen]);

  const handleRequestDeletion = async () => {
    if (step === 'initial') {
      setStep('confirm');
      return;
    }
    
    // This is the confirm step - actually call the API
    setLoading(true);
    try {
      const response = await requestAccountDeletion(reason || null);
      const scheduledDate = new Date(response.data.scheduled_deletion_at);
      setScheduledDate(scheduledDate);
      setStep('scheduled');
      // Dispatch custom event to notify banner to refresh
      window.dispatchEvent(new CustomEvent('accountDeletionScheduled'));
    } catch (error) {
      console.error('Failed to request account deletion:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to schedule account deletion. Please try again.';
      notify.error(errorMessage);
      setLoading(false);
    }
  };

  const handleCancelDeletion = async () => {
    // If we're on the scheduled step, transition to confirmation
    if (step === 'scheduled') {
      setPreviousStep('scheduled');
      setLoading(false); // Reset loading state when transitioning to confirmation
      setStep('cancelConfirm');
      return;
    }
    
    // Only proceed with API call if we're on the cancelConfirm step
    if (step !== 'cancelConfirm') {
      console.warn('handleCancelDeletion called from unexpected step:', step);
      return;
    }
    
    // This is the cancelConfirm step - actually call the API
    setLoading(true);
    try {
      await cancelAccountDeletion();
      setStep('initial');
      setScheduledDate(null);
      // Dispatch custom event to notify banner to refresh
      window.dispatchEvent(new CustomEvent('accountDeletionCancelled'));
      // Call onClose callback to notify parent component
      if (onClose) {
        onClose();
      }
    } catch (error) {
      console.error('Failed to cancel account deletion:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to cancel account deletion. Please try again.';
      notify.error(errorMessage);
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep('initial');
    setReason('');
    setPreviousStep(null);
    onClose();
  };

  if (!isOpen) return null;

  const renderContent = () => {
    switch (step) {
      case 'initial':
        return (
          <>
            <div className="ad-icon ad-icon-danger">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="ad-title">Delete Your Account</h3>
            <p className="ad-text">
              Requesting account deletion will schedule your account for deletion in 30 days.
              During this period, you can cancel the deletion request at any time.
            </p>
            <div className="ad-warning-box">
              <strong>Warning:</strong> Once your account is deleted, all your data will be 
              permanently removed and cannot be recovered. You can export your data from the login menu.
            </div>
            <div className="ad-form-group">
              <label>Reason for deletion (optional)</label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Tell us why you're leaving (optional)"
                rows="3"
              />
            </div>
            <div className="ad-buttons">
              <button className="ad-btn ad-btn-secondary" onClick={handleClose}>
                Cancel
              </button>
              <button className="ad-btn ad-btn-danger" onClick={handleRequestDeletion}>
                Request Deletion
              </button>
            </div>
          </>
        );

      case 'confirm':
        return (
          <div className="ad-confirm-box">
            <div className="ad-icon ad-icon-danger">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="ad-title">Confirm Account Deletion</h3>
            <p className="ad-text">
              Your account will be scheduled for deletion in 30 days. 
              You can cancel this request anytime before then.
            </p>
            <div className="ad-buttons">
              <button 
                className="ad-btn ad-btn-secondary" 
                onClick={() => setStep('initial')}
                disabled={loading}
              >
                Go Back
              </button>
              <button 
                className="ad-btn ad-btn-danger" 
                onClick={handleRequestDeletion}
                disabled={loading}
              >
                {loading ? 'Processing...' : 'Confirm Deletion'}
              </button>
            </div>
          </div>
        );

      case 'scheduled':
        return (
          <>
            <div className="ad-status-header">
              <div className="ad-icon ad-icon-warning">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="ad-title ad-title-warning">Account Scheduled for Deletion</h3>
            </div>
            <div className="ad-info-box">
              <p>
                Your account is scheduled to be deleted on{' '}
                <strong>{scheduledDate?.toLocaleDateString()}</strong>.
              </p>
              <p className="ad-text-small">
                You can cancel this deletion request at any time before the scheduled date.
              </p>
            </div>
            <div className="ad-buttons">
              <button className="ad-btn ad-btn-secondary" onClick={handleClose}>
                Close
              </button>
              <button className="ad-btn ad-btn-danger" onClick={handleCancelDeletion}>
                Cancel Deletion
              </button>
            </div>
          </>
        );

      case 'cancelConfirm':
        return (
          <div className="ad-confirm-box ad-confirm-box-info">
            <div className="ad-icon ad-icon-info">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="ad-title">Cancel Account Deletion?</h3>
            <p className="ad-text">
              Are you sure you want to cancel the account deletion request? 
              Your account will no longer be scheduled for deletion.
            </p>
            <div className="ad-buttons">
              <button 
                className="ad-btn ad-btn-secondary" 
                onClick={() => {
                  if (previousStep === 'scheduled') {
                    setStep('scheduled');
                    setPreviousStep(null);
                  } else {
                    handleClose();
                  }
                }}
                disabled={loading}
              >
                Go Back
              </button>
              <button 
                className="ad-btn ad-btn-primary" 
                onClick={handleCancelDeletion}
                disabled={loading}
              >
                {loading ? 'Cancelling...' : 'Confirm Cancel'}
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return ReactDOM.createPortal(
    <div className="ad-overlay" onClick={handleClose}>
      <div className="ad-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ad-header">
          <h2>Account Deletion</h2>
          <button className="ad-close" onClick={handleClose}>×</button>
        </div>
        <div className="ad-content">
          {renderContent()}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default AccountDeletion;